import { Fetcher, isProduction } from '@this/src/util';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import _ from 'lodash';
import type { Moment } from 'moment';
import moment from 'moment';
import Notification from '@this/src/notification';
import type { HotelOrderItemEanTotalArgs } from '@this/src/domain/arrangement/hotel_order_item_ean_total';
import HotelOrderItemEanTotal, {
  convertHotelOrderItemEanTotal
} from '@this/src/domain/arrangement/hotel_order_item_ean_total';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import { Button } from '@this/src/components/shared/ui/inputs/button';
import TotalSearchArea from './total_search_area';
import TotalResultTable from './total_result_table';
import UploadModal from '../upload_modal';

interface EanTotalResponse {
  records: HotelOrderItemEanTotalArgs[];
  supplied_item_name: string | null;
  latest_upload_action: {
    id: number;
    status: string;
    original_filename: string | null;
    created_at: string;
  } | null;
}

interface UploadActionShowResponse {
  upload_action: {
    id: number;
    status: string;
    original_filename: string | null;
    record_count: number;
    error: string | null;
    finished_at: string | null;
  };
}

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 30 * 60 * 1000;

const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

const isOngoingUploadStatus = (status: string | null) => status === 'unstarted' || status === 'started';

const uploadStatusLabel = (status: string) => {
  if (status === 'succeeded') return '完了';
  if (status === 'started') return '実行中';
  if (status === 'failed') return 'エラー';
  return '未開始';
};

const testBaseReportCsvFilename = (receiveDateFrom?: Moment, receiveDateTo?: Moment) => {
  const from = receiveDateFrom?.format('YYYY-MM-DD');
  const to = receiveDateTo?.format('YYYY-MM-DD');
  if (from && to) {
    return `ean_hotel_test_base_report_${from}_${to}.csv`;
  }
  if (from) {
    return `ean_hotel_test_base_report_${from}.csv`;
  }
  return 'ean_hotel_test_base_report.csv';
};

const Total = observer(() => {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [eanTotals, setEanTotals] = useState<HotelOrderItemEanTotal[]>([]);
  const [suppliedItemName, setSuppliedItemName] = useState<string>('');
  const [latestUploadActionId, setLatestUploadActionId] = useState<number | null>(null);
  const [latestUploadStatus, setLatestUploadStatus] = useState<string | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [testBaseReportCsvLoading, setTestBaseReportCsvLoading] = useState(false);
  const [receiveDateFrom, setReceiveDateFrom] = useState<Moment | undefined>(
    utils.getParamMoment('receive_date_from') ?? moment()
  );
  const [receiveDateTo, setReceiveDateTo] = useState<Moment | undefined>(
    utils.getParamMoment('receive_date_to') ?? undefined
  );
  const fetchingRef = useRef(false);

  const fetchTodos = useCallback(() => {
    if (fetchingRef.current) {
      fetchingRef.current = false;
      return;
    }
    setIsLoading(true);
    setEanTotals([]);

    const params = {
      receive_date_from: receiveDateFrom?.format('YYYY-MM-DD'),
      receive_date_to: receiveDateTo?.format('YYYY-MM-DD')
    };
    _.each(params, (value, key) => {
      if (value === undefined) {
        delete params[key as keyof typeof params];
      }
    });
    const queryParams = new URLSearchParams();
    queryParams.set('tab', 'AUTO_RESERVATION_INVENTORY');
    queryParams.set('sub_tab', 'EAN_HOTEL');
    queryParams.set('ean_hotel_type', 'TOTAL');
    if (receiveDateFrom) {
      queryParams.set('receive_date_from', receiveDateFrom.format('YYYY-MM-DD'));
    }
    if (receiveDateTo) {
      queryParams.set('receive_date_to', receiveDateTo.format('YYYY-MM-DD'));
    }
    history.push(`/arrangement/todo_list?${queryParams.toString()}`);

    Fetcher.get<EanTotalResponse>('/arrangement/auto_reservation_inventory/ean_hotel/total.json', params)
      .then(res => {
        setEanTotals(res.records.map(args => new HotelOrderItemEanTotal(convertHotelOrderItemEanTotal(args))));
        setSuppliedItemName(res.supplied_item_name || '');
        setLatestUploadActionId(res.latest_upload_action?.id ?? null);
        setLatestUploadStatus(res.latest_upload_action?.status || null);
      })
      .catch(() => {
        Notification.error('データの取得に失敗しました');
      })
      .finally(() => setIsLoading(false));
  }, [receiveDateFrom, receiveDateTo, history]);

  useEffect(() => {
    fetchTodos();
  }, [fetchTodos]);

  useEffect(() => {
    if (latestUploadActionId == null || !isOngoingUploadStatus(latestUploadStatus)) {
      return () => {};
    }

    let cancelled = false;

    const pollUploadAction = async () => {
      const deadline = Date.now() + POLL_TIMEOUT_MS;
      while (!cancelled && Date.now() < deadline) {
        await sleep(POLL_INTERVAL_MS);
        if (cancelled) return;

        try {
          const res = await Fetcher.get<UploadActionShowResponse>(
            `/arrangement/auto_reservation_inventory/ean_hotel/upload/${latestUploadActionId}.json`
          );
          const { status } = res.upload_action;
          setLatestUploadStatus(status);
          if (status === 'succeeded' || status === 'failed') {
            fetchTodos();
            return;
          }
        } catch {
          // 単発エラーは継続してリトライ
        }
      }
    };

    pollUploadAction();

    return () => {
      cancelled = true;
    };
  }, [latestUploadActionId, fetchTodos]);

  const testBaseReportCsvUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (receiveDateFrom) {
      params.set('receive_date_from', receiveDateFrom.format('YYYY-MM-DD'));
    }
    if (receiveDateTo) {
      params.set('receive_date_to', receiveDateTo.format('YYYY-MM-DD'));
    }
    const query = params.toString();
    return `/arrangement/auto_reservation_inventory/ean_hotel/total/test_base_report_csv${
      query ? `?${query}` : ''
    }`;
  }, [receiveDateFrom, receiveDateTo]);

  const handleTestBaseReportCsvDownload = useCallback(async () => {
    setTestBaseReportCsvLoading(true);
    try {
      const response = await fetch(testBaseReportCsvUrl, { credentials: 'same-origin' });
      if (!response.ok) {
        throw new Error('download failed');
      }
      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/);
      const filename = filenameMatch?.[1] ?? testBaseReportCsvFilename(receiveDateFrom, receiveDateTo);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      Notification.error('テスト用ベースCSVのダウンロードに失敗しました');
    } finally {
      setTestBaseReportCsvLoading(false);
    }
  }, [receiveDateFrom, receiveDateTo, testBaseReportCsvUrl]);

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
        <Button onClick={() => setShowUploadModal(true)}>CSVアップロード</Button>
        {!isProduction() && (
          <Button onClick={handleTestBaseReportCsvDownload} loading={testBaseReportCsvLoading}>
            テスト用ベースCSV
          </Button>
        )}
        {latestUploadStatus && (
          <span style={{ fontSize: '12px', color: '#666' }}>
            最新ジョブ: {uploadStatusLabel(latestUploadStatus)}
          </span>
        )}
      </div>
      <TotalSearchArea
        receiveDateFrom={receiveDateFrom}
        receiveDateTo={receiveDateTo}
        suppliedItemName={suppliedItemName}
        setReceiveDateFrom={setReceiveDateFrom}
        setReceiveDateTo={setReceiveDateTo}
      />
      {isLoading ? <Loading /> : <TotalResultTable hotelOrderItemEanTotals={eanTotals} />}
      {showUploadModal && (
        <UploadModal onClose={() => setShowUploadModal(false)} onUploaded={() => fetchTodos()} />
      )}
    </>
  );
});

export default Total;
